import React, { createContext, useReducer, useContext } from 'react'
import PropTypes from 'prop-types'
import { noop, cloneDeep } from 'lodash'

const actionTypes = {
  CLEAR_QUEUE: 'CLEAR_QUEUE',
  PUSH_QUEUE_ITEM: 'PUSH_QUEUE_ITEM',
}

const ApplicationQueuesContext = createContext({
  postAuthentication: {
    items: [],
    push: noop,
    clear: noop,
    process: noop,
  },
  postOnboarding: {
    items: [],
    push: noop,
    clear: noop,
    process: noop,
  },
})

export const applicationQueuesReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.PUSH_QUEUE_ITEM: {
      const { targetQueue, item } = action
      const updatedQueue = cloneDeep(state[targetQueue].items)
      updatedQueue.push(item)
      return {
        ...state,
        [targetQueue]: {
          ...state[targetQueue],
          items: updatedQueue,
        },
      }
    }
    case actionTypes.CLEAR_QUEUE: {
      const { targetQueue } = action
      return {
        ...state,
        [targetQueue]: {
          ...state[targetQueue],
          items: [],
        },
      }
    }
    default: {
      return state
    }
  }
}

function ApplicationQueuesProvider({ children }) {
  const [state, dispatch] = useReducer(applicationQueuesReducer, {
    postAuthentication: {
      items: [],
    },
    postOnboarding: {
      items: [],
    },
  })

  const pushQueueItem = (targetQueue, item) => {
    dispatch({ type: actionTypes.PUSH_QUEUE_ITEM, item, targetQueue })
  }

  const clearQueue = targetQueue => {
    dispatch({ type: actionTypes.CLEAR_QUEUE, targetQueue })
  }

  const processQueue = (targetQueue, authType, user) => {
    state[targetQueue].items.forEach(item =>
      item.length === 1
        ? item[0].call(null, authType, user)
        : item[0].call(null, ...item[1], authType, user)
    )
    dispatch({ type: actionTypes.CLEAR_QUEUE, targetQueue })
  }

  const context = {
    postAuthentication: {
      push: item => pushQueueItem('postAuthentication', item),
      clear: () => clearQueue('postAuthentication'),
      process: (authType, user) =>
        processQueue('postAuthentication', authType, user),
    },
    postOnboarding: {
      push: item => pushQueueItem('postOnboarding', item),
      clear: () => clearQueue('postOnboarding'),
      process: (authType, user) =>
        processQueue('postOnboarding', authType, user),
    },
  }

  return (
    <ApplicationQueuesContext.Provider value={context}>
      {children}
    </ApplicationQueuesContext.Provider>
  )
}

ApplicationQueuesProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

const useApplicationQueues = () => useContext(ApplicationQueuesContext)

export {
  ApplicationQueuesContext,
  ApplicationQueuesProvider,
  useApplicationQueues,
}
